import type { AnyAction, Middleware } from 'redux';
import { actionLogger, recorderLogger } from '../../../utils/logger/loggerFactory';
import { types as callTypes } from '../../actions/backendCall';
import { types as deviceTypes } from '../../actions/backendDevice';
import { types as asrTypes } from '../../actions/backendAsr';
import { types as commonTypes } from '../../actions/backendCommon';
import { types as mainTypes } from '../../actions/backendMain';
import {
    SET_BLACKLIST,
    SET_ELAPSED_SECONDS,
    SET_LOUDNESS,
    SET_NEW_SPEECH_RATE,
    SET_PHRASE_INTEGRATION_WORDS,
    SET_SPEECH_PACE,
    SET_SPEECH_RATE,
    SET_SPEECH_RATIO,
    SET_TIMELINE_TRANSCRIPT,
    SET_WHITELIST
} from '../../actions/actionTypes/trainer';
import { DOMAIN_CALL_DATA } from '../../actions/actionTypes/domain';
import type { BackendLogLevel } from '../../logDispatch';

const hiddenBackendActionTypes = [
    deviceTypes.AUDIO_CHUNK_RECEIVED,
    asrTypes.SOUND_CHUNK,
    asrTypes.SET_SESSION_DURATION,
    asrTypes.MIC_UPDATE_ASR_SPEECH_PACE,
    asrTypes.SPEAKER_UPDATE_ASR_RESULTS,
    asrTypes.SPEAKER_UPDATE_ASR_VOICE_METRICS,
    asrTypes.MIC_UPDATE_ASR_RESULTS,
    asrTypes.MIC_UPDATE_PHRASE_SPOTTING_RESULTS,
    asrTypes.MIC_UPDATE_ASR_VOICE_METRICS,
    callTypes.CALL_TIMER_UPDATED,
    commonTypes.UPDATE_RELOAD_COUNT_DOWN,
    mainTypes.EVENT_FORWARDED
];

const hiddenDerivedUiActionTypes = [
    SET_ELAPSED_SECONDS,
    SET_BLACKLIST,
    SET_WHITELIST,
    SET_LOUDNESS,
    SET_TIMELINE_TRANSCRIPT,
    SET_NEW_SPEECH_RATE,
    SET_SPEECH_PACE,
    SET_SPEECH_RATE,
    SET_SPEECH_RATIO,
    SET_PHRASE_INTEGRATION_WORDS
];

const hiddenActionTypes = new Set([...hiddenBackendActionTypes, ...hiddenDerivedUiActionTypes]);

// Keep one aggregated signal for ASR/call metric updates, but only occasionally.
const throttledActionIntervals = new Map<string, number>([[DOMAIN_CALL_DATA, 15000]]);

const lastLoggedAtByType = new Map<string, number>();

export const shouldLogAction = (type: string, now = Date.now()) => {
    if (hiddenActionTypes.has(type)) {
        return false;
    }

    const throttleMs = throttledActionIntervals.get(type);

    if (throttleMs === undefined) {
        return true;
    }

    const lastLoggedAt = lastLoggedAtByType.get(type);
    if (lastLoggedAt !== undefined && now - lastLoggedAt < throttleMs) {
        return false;
    }

    lastLoggedAtByType.set(type, now);
    return true;
};

export const resetActionLogThrottle = () => {
    lastLoggedAtByType.clear();
};

if (window.electronAPI?.onLogMessage) {
    window.electronAPI.onLogMessage(([type, msg]: [BackendLogLevel, unknown]) => {
        const handlers: Record<BackendLogLevel, (msg: unknown) => void> = {
            debug: recorderLogger.debug.bind(recorderLogger),
            info: recorderLogger.log.bind(recorderLogger),
            log: recorderLogger.log.bind(recorderLogger),
            warn: recorderLogger.warn.bind(recorderLogger),
            error: recorderLogger.error.bind(recorderLogger)
        };
        handlers[type]?.(msg);
    });
}

const logger: Middleware = () => {
    return next => action => {
        const typedAction = action as AnyAction;
        const { type, payload, error } = typedAction || {};

        if (error) {
            actionLogger.error(type, error.message, error);
            return next(action);
        }

        if (shouldLogAction(type)) {
            actionLogger.log(type, payload);
        }

        return next(action);
    };
};

export default logger;
